import { useState } from 'react';
import { Button, Flex, Form, Input, Typography } from 'antd';
import type { FormProps } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// services
import NotificationService from 'core/services/notification';
import { checkPhoneValidation } from 'utils/validators/phone';

// components
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import LanguageButton from 'components/language-button/language-button';
import OTP from 'components/otp/otp';
import SuccessComponent from 'components/success/success';
import PageTitle from 'components/title/title';

// styles
import styles from './reset-password.module.scss';
import darkLogo from 'assets/images/logo-dark.svg';
import FormPassword from 'components/formPassword/formPassword';
import { forgetPassword, resetPassword, verifyOtp } from '../services/reset-password';
import { resendOtp } from '../../register/services/contactProfileService';
import Recaptcha from 'components/recaptcha/recaptcha';
import BackButton from 'components/backButton/backButton';

function ResetPasswordPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [phoneForm] = Form.useForm();
	const [passwordForm] = Form.useForm();

	const [isLoading, setIsLoading] = useState(false);
	const [isResendLoading, setIsResendLoading] = useState(false);
	const [invalidOtp, setInvalidOtp] = useState(false);

	const [currentStep, setCurrentStep] = useState<'phone' | 'otp' | 'password' | 'success'>('phone');

	const [otpTtl, setOtpTtl] = useState<number>(60);

	// for verify otp and reset password step
	const [phoneNumber, setPhoneNumber] = useState<string>('');
	const [phoneCountryCode, setPhoneCountryCode] = useState<string>('');
	const [otp, setOtp] = useState<string>('');

	type phoneFieldType = {
		phone_number: string;
		phone_country_code: string;
		token: string;
	};

	type passwordFieldType = {
		password: string;
		password_confirmation: string;
	};

	const validatePassword = (_: any, value: string) => {
		if (!value || passwordForm.getFieldValue('password') === value) {
			return Promise.resolve();
		}
		return Promise.reject(new Error(t('COMMON.PASSWORDS_NOT_MATCH')));
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		phoneForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const validatePhone = (_: any, value: string) => {
		if (checkPhoneValidation(phoneForm.getFieldValue('phone_country_code'),value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const onPhoneFinish: FormProps<phoneFieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			setPhoneNumber(values.phone_number);
			setPhoneCountryCode(values.phone_country_code);

			const { otp_ttl } = await forgetPassword(values);

			setOtpTtl(otp_ttl);

			setIsLoading(false);
			setCurrentStep('otp');
		} catch (error: any) {
			setIsLoading(false);

			if (error?.response?.status === 401) NotificationService.error(t('COMMON.ERROR'), t('RESET_PASSWORD.PHONE_NOT_REGISTERED'));
			else NotificationService.error(t('COMMON.ERROR'));
		}
	};

	const onResendOtp = async (token: string) => {
		try {
			setIsResendLoading(true);
			await resendOtp(token);
			NotificationService.success(t('COMMON.SUCCESS'), t('REGISTER.OTP_RESENT'));
			setIsResendLoading(false);
		} catch (error) {
			setIsResendLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const onOtpFinish = async (otp: string) => {
		try {
			setIsLoading(true);

			setOtp(otp);

			await verifyOtp({ phone_number: phoneNumber, phone_country_code: phoneCountryCode, otp: otp });

			setIsLoading(false);
			setCurrentStep('password');
		} catch (error: any) {
			setIsLoading(false);
			setInvalidOtp(true);
		}
	};

	const onPasswordFinish: FormProps<passwordFieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			await resetPassword({ phone_number: phoneNumber, phone_country_code: phoneCountryCode, otp: otp, ...values });

			setIsLoading(false);
			setCurrentStep('success');

			setTimeout(() => {
				navigate('/');
			}, 5000);
		} catch (error: any) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('REGISTER.INVALID_OTP'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.RESET_PASSWORD')} />

			<Flex vertical style={{ minHeight: '100vh', width: '100vw' }}>
				<motion.div
					initial={{ opacity: 0, transform: 'translateY(-100%)' }}
					animate={{ opacity: 1, transform: 'translateY(0%)' }}
					transition={{ duration: 0.5 }}>
					<header className="p-3 p-md-5 d-flex justify-content-between align-items-center">
						<Link to="/login">
							<img style={{ width: 200 }} src={darkLogo} />
						</Link>
						<Flex gap={10}>
							<Button color="primary" variant="filled" onClick={() => navigate('/login')}>
								{t('RESET_PASSWORD.HOME')}
							</Button>
							<LanguageButton />
						</Flex>
					</header>
				</motion.div>

				<main className="mt-5" style={{ minHeight: 320 }}>
					{/* phone number div */}
					{currentStep === 'phone' && (
						<motion.div
							initial={{ opacity: 0, transform: 'translateY(-50px)' }}
							animate={{ opacity: 1, transform: 'translateY(0px)' }}
							exit={{ opacity: 0, transform: 'translateY(-50px)' }}
							transition={{ delay: 0.3, duration: 0.5 }}
							key="phoneStep">
							<div className={styles.phoneDiv}>
								<h3 className={`mb-5`}>{t('RESET_PASSWORD.ENTER_PHONE')}</h3>
								<Form form={phoneForm} onFinish={onPhoneFinish}>
									<Form.Item
										name="phone_number"
										style={{ maxWidth: 'unset' }}
										className="mb-4"
										rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
										<PhoneInputComponent onChange={onChangePhone} labeled={false} />
									</Form.Item>

									<Form.Item name="phone_country_code" hidden>
										<Input />
									</Form.Item>

									<Form.Item name="recaptcha_token" rules={[{ required: true, message: t('COMMON.RECAPTCHA_REQUIRED') }]}>
										<Recaptcha centered onSubmit={(token) => {phoneForm.setFieldValue('recaptcha_token', token)}} />
									</Form.Item>

									<Button block type="primary" htmlType="submit" loading={isLoading}>
										{t('RESET_PASSWORD.SEND_OTP')}
									</Button>
								</Form>
							</div>
						</motion.div>
					)}

					{/* OTP div */}
					{currentStep === 'otp' && (
						<motion.div
							initial={{ opacity: 0, transform: 'translateY(-50px)' }}
							animate={{ opacity: 1, transform: 'translateY(0px)' }}
							exit={{ opacity: 0, transform: 'translateY(-50px)' }}
							transition={{ delay: 0.3, duration: 0.5 }}
							key="otpStep">
							<div className={styles.otpDiv}>
								<h3 className={`mb-5`}>{t('RESET_PASSWORD.VERIFY_PHONE')}</h3>

								<OTP
									isLoading={isLoading}
									onConfirm={onOtpFinish}
									ttl={otpTtl}
									onResend={onResendOtp}
									isResendLoading={isResendLoading}
									isInvalid={invalidOtp}
									onChange={() => setInvalidOtp(false)}
								/>
							</div>
						</motion.div>
					)}

					{/* new password div */}
					{currentStep === 'password' && (
						<motion.div
							initial={{ opacity: 0, transform: 'translateY(-50px)' }}
							animate={{ opacity: 1, transform: 'translateY(0px)' }}
							exit={{ opacity: 0, transform: 'translateY(-50px)' }}
							transition={{ delay: 0.3, duration: 0.5 }}
							key="passwordStep">
							<div className={styles.passwordDiv}>
								<h3 className={`mb-5 text-center`}>{t('RESET_PASSWORD.ENTER_PASSWORD')}</h3>
								<Form form={passwordForm} onFinish={onPasswordFinish}>
									<FormPassword
										fullWidth
										name="password"
										label={t('COMMON.PASSWORD')}
										rules={[
											{ required: true, message: t('COMMON.PASSWORD_REQUIRED') },
											{ min: 8, message: t('COMMON.PASSWORD_MIN_8') },
										]}
									/>

									<div className="my-4">
										<FormPassword
											fullWidth
											name="password_confirmation"
											label={t('COMMON.PASSWORD_CONFIRMATION')}
											rules={[{ required: true, message: t('COMMON.PASSWORD_CONFIRMATION_REQUIRED') }, { validator: validatePassword }]}
										/>
									</div>

									<Form.Item>
										<Button block type="primary" htmlType="submit" loading={isLoading}>
											{t('COMMON.CONFIRM')}
										</Button>
									</Form.Item>
								</Form>
							</div>
						</motion.div>
					)}

					{/* success div */}
					{currentStep === 'success' && (
						<div className={styles.successDiv}>
							<motion.div
								initial={{ opacity: 0, transform: 'translateY(-50px)' }}
								animate={{ opacity: 1, transform: 'translateY(0px)' }}
								transition={{ delay: 0.3, duration: 0.5 }}>
								<SuccessComponent />
								<h5 className="mt-3">{t('RESET_PASSWORD.PASSWORD_CHANGED')}</h5>
								<p className="text-light">{t('RESET_PASSWORD.REDIRECT_TO_HOME')}</p>
							</motion.div>
						</div>
					)}
				</main>

				<motion.div
					initial={{ opacity: 0, transform: 'translateY(-50px)' }}
					animate={{ opacity: 1, transform: 'translateY(0px)' }}
					transition={{ delay: 0.5, duration: 0.5 }}>
					<footer className="p-3 text-center" style={{ marginTop: 50 }}>
						{t('COMMON.POWERED_BY')} <span className="fw-bold">{t('COMMON.TAKHAROJ_FINANCE')}</span> {new Date().getFullYear()}{' '}
						{t('COMMON.HEFTH_PLATFORM')}.
					</footer>
				</motion.div>
			</Flex>
		</>
	);
}

export default ResetPasswordPage;
